import { DEFAULT_CURRENCY_FORMATTER } from "@redotech/money/util";
import type { BackInStockProductGroup } from "@redotech/redo-customer-common/customer-product-notification/customer-product-notification";
import ChevronLeftIcon from "@redotech/redo-icons/chevron-left.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useState } from "react";
import type { BisFilterProps } from "./filter-props";
import { ProductDetailHeader, StatItem } from "./product-detail-header";
import { SubscribersList } from "./subscribers-list";
import { VariantDetail } from "./variant-detail";
import { VariantsTab } from "./variants-tab";

const DETAIL_TABS = [
  { key: "subscribers" as const, label: "Subscribers" },
  { key: "variants" as const, label: "Variants" },
];

export const ProductGroupDetail = memo(function ProductGroupDetail({
  product,
  subscriberSearch,
  setSubscriberSearch,
  refetchAllProducts,
  ...filterProps
}: {
  product: BackInStockProductGroup;
  subscriberSearch: string;
  setSubscriberSearch: (q: string) => void;
  refetchAllProducts: () => void;
} & BisFilterProps) {
  const [detailTab, setDetailTab] = useState<"subscribers" | "variants">(
    "subscribers",
  );
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    null,
  );

  const selectedVariantDetail = selectedVariantId
    ? product.variants.find((v) => v.variantId === selectedVariantId)
    : undefined;

  if (selectedVariantDetail) {
    return (
      <Flex dir="column" flex="1" gap="none" overflow="hidden">
        <Flex px="lg" py="sm">
          <RedoButton
            hierarchy="tertiary"
            IconLeading={ChevronLeftIcon}
            onClick={() => setSelectedVariantId(null)}
            size="sm"
            text={`Back to ${product.name}`}
          />
        </Flex>
        <VariantDetail
          {...filterProps}
          refetchAllProducts={refetchAllProducts}
          setSubscriberSearch={setSubscriberSearch}
          subscriberSearch={subscriberSearch}
          variant={{
            productId: product.productId,
            variantId: selectedVariantDetail.variantId,
            name: `${product.name} - ${selectedVariantDetail.title}`,
            sku: selectedVariantDetail.sku,
            imageUrl: product.imageUrl,
            waitingCount: selectedVariantDetail.waitingCount,
            notifiedCount: selectedVariantDetail.notifiedCount,
            price: selectedVariantDetail.price,
          }}
        />
      </Flex>
    );
  }

  return (
    <Flex dir="column" flex="1" gap="none" overflow="hidden">
      <ProductDetailHeader
        imageUrl={product.imageUrl}
        name={product.name}
        stats={
          <Flex align="center" gap="3xl">
            <StatItem label="waiting" value={product.waitingCount.toString()} />
            <StatItem
              color="success"
              label="potential revenue"
              value={DEFAULT_CURRENCY_FORMATTER.format(
                product.potentialRevenue,
              )}
            />
          </Flex>
        }
        subtitle={`${product.variantCount} variants \u2022 ${product.inStockCount} in stock \u2022 ${product.outOfStockCount} out of stock`}
      />

      <Divider />

      <Flex px="xl" py="md">
        <RedoHorizontalTabs
          drawBottomLine
          pl="none"
          selectedTab={detailTab}
          setSelectedTab={setDetailTab}
          size="xs"
          tabs={DETAIL_TABS}
        />
      </Flex>

      {detailTab === "subscribers" && (
        <SubscribersList
          {...filterProps}
          productId={product.productId}
          setSubscriberSearch={setSubscriberSearch}
          showVariantColumn
          subscriberSearch={subscriberSearch}
        />
      )}

      {detailTab === "variants" && (
        <VariantsTab onVariantClick={setSelectedVariantId} product={product} />
      )}
    </Flex>
  );
});
